/* globals __DEV__ */
import _ from "lodash";
import ko from "knockout";
import qualityManagementService from "services/qualityManagementService";
import qualityAuthorization from "services/qualityAuth";
import authService from "services/authService";
import errorCallbackFactory from "util/errorCallbackFactory";
import ViewModel from "viewModels/ViewModel";
import EditCalibrationView from "viewModels/EditCalibrationView";
import Calibration from "models/Calibration";
import toastService from "services/toastService";
import loggingService from "services/loggingService";
import navHelper from "services/navHelper";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import { addQueryParamToWindow } from "util/uriUtil";
import EvaluationStatus from "enums/composer/EvaluationStatus";

const EMPTYSCORE = " - ";

const CalibrationResultsView = ViewModel.extend(function (
    calibrationId,
    deleteCalibrationCallback
) {
    const self = this;

    let evaluationHref;

    self.isLoading = ko.observable(true);
    self.calibrationResultsGrid = ko.observableArray();
    self.evaluationFormName = ko.observable();
    self.calibration = ko.observable();
    self.editCalibrationView = ko.observable();
    self.highScore = ko.observable();
    self.lowScore = ko.observable();
    self.averageScore = ko.observable();
    self.variance = ko.observable();
    self.canDeleteCalibration = qualityAuthorization.canDeleteCalibration();
    self.isDeleteCalibrationInProgress = ko.observable(false);

    const submitCalibration = function (options) {
        const calibrationUpdate = self.editCalibrationView().getCalibration();
        const responseValidator = self
            .editCalibrationView()
            .getResponseValidator();
        if (calibrationUpdate) {
            self.isLoading(true);
            qualityManagementService
                .putCalibration(calibrationUpdate)
                .then(function (response) {
                    responseValidator.handleSuccess();
                    self.calibration(new Calibration(response));
                    if (options && options.success) {
                        options.success();
                    }
                })
                .catch(
                    responseValidator.createErrorCallback({
                        namespace:
                            "calibrationResults.updateCalibrationRequest",
                    })
                )
                .finally(function () {
                    self.isLoading(false);
                });
        }
    };

    self.isEditing = ko.observable(false);
    self.subscribe(self.isEditing, function (newValue) {
        if (newValue) {
            self.editCalibrationView().reset();
        }
    });

    self.applyClick = function () {
        submitCalibration({
            success: function () {
                self.isEditing(false);
            },
        });
    };

    self.showDeleteCalibrationPrompt = function () {
        const modal = document.getElementById("deleteCalibrationPrompt");
        if (modal && typeof modal.showModal === "function") {
            modal.showModal();
        }
    };

    self.closeDeleteCalibrationPrompt = function () {
        const modal = document.getElementById("deleteCalibrationPrompt");
        if (modal && typeof modal.close === "function") {
            modal.close();
        }
    };

    self.deleteCalibration = function () {
        self.isDeleteCalibrationInProgress(true);
        self.closeDeleteCalibrationPrompt();
        const unwrapped = ko.unwrap(self.calibration);
        deleteCalibrationCallback(
            unwrapped.id,
            self
                .calibration()
                .evaluations()
                .map(({ id }) => id)
        )
            .then(function () {
                self.isDeleteCalibrationInProgress(false);
            })
            .catch(function (error) {
                self.isDeleteCalibrationInProgress(false);
                loggingService.error("Error deleting calibration:", error);
            });
    };

    const getEvaluationHref = function (evaluation) {
        return navHelper.asHostHash(
            addQueryParamToWindow({ evaluationId: evaluation.id })
        );
    };

    const aggregateEvaluationData = function (
        evaluationForm,
        scoreRows,
        evaluation,
        evaluatorId
    ) {
        // Generate href for navigation using existing getEvaluationHref logic
        // Handle both regular evaluators and scoring index cases
        const evaluationHref = getEvaluationHref(evaluation);

        scoreRows["evaluation-total"][evaluatorId] = {
            score: EMPTYSCORE,
            comments: "",
            href: evaluationHref,
        };
        scoreRows["evaluation-total"].style = "evaluation-total-score-row";

        /* -- Get evaluation comments if it exists -- */
        if (
            evaluation.hasOwnProperty("answers") &&
            evaluation.answers.comments !== ""
        ) {
            //Get the comments for the overall evaluation
            scoreRows["evaluation-total"][evaluatorId].comments =
                evaluation.answers.comments;
        }

        _.each(evaluationForm.questionGroups, function (questionGroup) {
            scoreRows[questionGroup.id][evaluatorId] = {
                score: EMPTYSCORE,
                comments: "",
                href: evaluationHref,
            };

            // Recurse down to initialize question scores
            _.each(questionGroup.questions, function (question) {
                scoreRows[question.id][evaluatorId] = {
                    score: EMPTYSCORE,
                    comments: "",
                    href: evaluationHref,
                };
            });
        });

        if (
            evaluation.status === EvaluationStatus.FINISHED &&
            evaluation.answers
        ) {
            // Fill in the score rows for the evaluation grid.
            const totalScoreString = ko.i18n.formatNumber(
                evaluation.answers.totalScore / 100,
                {
                    style: "percent",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }
            );

            scoreRows["evaluation-total"][evaluatorId].score = totalScoreString;

            let totalGroupsScore = 0;
            let maxTotalGroupsScore = 0;

            _.each(
                evaluation.answers.questionGroupScores,
                function (questionGroupScore) {
                    if (questionGroupScore.markedNA) {
                        scoreRows[questionGroupScore.questionGroupId][
                            evaluatorId
                        ].score = ko.i18n.renderString(
                            "playback.calibrationSummary.score.na"
                        );
                    } else if (questionGroupScore.totalScore !== undefined) {
                        scoreRows[questionGroupScore.questionGroupId][
                            evaluatorId
                        ].score = questionGroupScore.totalScore
                            .toFixed(2)
                            .replace(/\.?0+$/, "");
                        totalGroupsScore += questionGroupScore.totalScore;
                        maxTotalGroupsScore += questionGroupScore.maxTotalScore;

                        const formattedTotalScore = totalGroupsScore
                            .toFixed(2)
                            .replace(/\.?0+$/, "");
                        const formattedMaxTotalScore = maxTotalGroupsScore
                            .toFixed(2)
                            .replace(/\.?0+$/, "");
                        scoreRows["evaluation-total"][
                            evaluatorId
                        ].score = `${formattedTotalScore}/${formattedMaxTotalScore} (${totalScoreString})`;
                    }

                    const evaluationQuestionGroup = _.find(
                        self.calibration().evaluationForm().questionGroups(),
                        (evaluationQuestionGroup) =>
                            evaluationQuestionGroup.id ===
                            questionGroupScore.questionGroupId
                    );

                    _.each(
                        questionGroupScore.questionScores,
                        function (questionScore) {
                            if (
                                questionScore.markedNA ||
                                questionGroupScore.markedNA
                            ) {
                                scoreRows[questionScore.questionId][
                                    evaluatorId
                                ].score = ko.i18n.renderString(
                                    "playback.calibrationSummary.score.na"
                                );
                            } else if (questionScore.score !== undefined) {
                                let scoreValue = questionScore.score;

                                // For multi-select questions, sum up the scores from multipleSelectQuestionOptionScores
                                if (
                                    questionScore.hasOwnProperty(
                                        "multipleSelectQuestionOptionScores"
                                    ) &&
                                    questionScore
                                        .multipleSelectQuestionOptionScores
                                        .length > 0
                                ) {
                                    scoreValue = _.sumBy(
                                        questionScore.multipleSelectQuestionOptionScores,
                                        "score"
                                    );
                                }

                                scoreRows[questionScore.questionId][
                                    evaluatorId
                                ].score = (
                                    (scoreValue *
                                        evaluationQuestionGroup.weight()) /
                                    100.0
                                )
                                    .toFixed(2)
                                    .replace(/\.?0+$/, "");
                            }
                            if (
                                questionScore.hasOwnProperty("comments") &&
                                questionScore.comments !== ""
                            ) {
                                scoreRows[questionScore.questionId][
                                    evaluatorId
                                ].comments = questionScore.comments;
                            }
                        }
                    );
                }
            );
        }
    };

    const buildRows = function (keys, rows) {
        const calibrationRows = [];
        _.each(rows, function (row) {
            const newRow = {};
            _.each(keys, function (key) {
                if (row.hasOwnProperty(key)) {
                    newRow[key] = row[key];
                    newRow.rowStyle = row.style;
                    if (row.comments) {
                        newRow.comments = row.comments;
                    }
                } else {
                    newRow[key] = EMPTYSCORE;
                }
            });
            calibrationRows.push(newRow);
        });
        return calibrationRows;
    };

    const createCellTemplate = function (id, href) {
        return `
            <a data-evaluator-id="${id}" href="#" data-bind="
                text: $data['${id}'].score,
                navigateTo: '${href}'
            "></a>
            <div class="calibration-evaluation-comments" data-bind="if: $data['${id}'].comments">
                <i class="fa fa-comments" data-bind="tooltip:$data['${id}'].comments"></i>
            </div>
        `;
    };

    const buildCalibrationGridData = function (calibration) {
        const unwrappedCalibration = ko.toJS(calibration);
        if (unwrappedCalibration.evaluationForm) {
            self.evaluationFormName(unwrappedCalibration.evaluationForm.name);
            self.highScore(unwrappedCalibration.highScore);
            self.lowScore(unwrappedCalibration.lowScore);
            self.averageScore(unwrappedCalibration.averageScore);
            self.variance(self.highScore() - self.lowScore());
            let evaluatorColumns;
            let evaluatorsToSort = unwrappedCalibration.evaluators;
            let expertEvaluator;
            let columns;

            self.calibrationResultsGrid({
                columns: ko.observableArray([]),
                rows: ko.observableArray([]),
            });

            try {
                if (unwrappedCalibration.expertEvaluator) {
                    expertEvaluator = _.find(
                        unwrappedCalibration.evaluators,
                        function (evaluator) {
                            return (
                                unwrappedCalibration.expertEvaluator.id ===
                                evaluator.id
                            );
                        }
                    );
                    evaluatorsToSort = _.reject(
                        unwrappedCalibration.evaluators,
                        function (evaluator) {
                            return (
                                unwrappedCalibration.expertEvaluator &&
                                unwrappedCalibration.expertEvaluator.id ===
                                    evaluator.id
                            );
                        }
                    );
                }

                /* -- Create the columns for dataTable -- */
                evaluatorColumns = _.map(
                    _.sortBy(evaluatorsToSort, "name"),
                    function (evaluator) {
                        const evaluatorEvaluation = _.find(
                            calibration.evaluations(),
                            function (evaluation) {
                                return evaluation.evaluator.id === evaluator.id;
                            }
                        );
                        evaluationHref = getEvaluationHref(evaluatorEvaluation);
                        const evaluatorId = evaluator.id;
                        return {
                            label: evaluator.name,
                            id: evaluatorId,
                            class: "evaluator-column",
                            text: true,
                            cellTemplate: createCellTemplate(
                                evaluatorId,
                                evaluationHref
                            ),
                        };
                    }
                );

                if (expertEvaluator) {
                    const expertEvaluatorId = expertEvaluator.id;
                    const evaluatorEvaluation = _.find(
                        calibration.evaluations(),
                        function (evaluation) {
                            return (
                                evaluation.evaluator.id === expertEvaluator.id
                            );
                        }
                    );
                    evaluationHref = getEvaluationHref(evaluatorEvaluation);
                    const columnText = ko.i18n.renderString(
                        "calibrationResults.columnName.expertEvaluator",
                        { name: expertEvaluator.name }
                    );
                    evaluatorColumns.unshift({
                        label: columnText,
                        id: expertEvaluatorId,
                        class: "evaluator-column",
                        text: true,
                        cellTemplate: createCellTemplate(
                            expertEvaluatorId,
                            evaluationHref
                        ),
                    });
                } else if (unwrappedCalibration.scoringIndex) {
                    const scoringIndexId = unwrappedCalibration.scoringIndex.id;
                    const scoringIndexHref = getEvaluationHref(
                        unwrappedCalibration.scoringIndex
                    );
                    evaluatorColumns.unshift({
                        label: ko.i18n.renderString(
                            "calibrationResults.columnName.scoringIndex"
                        ),
                        text: true,
                        id: scoringIndexId,
                        class: "calibration-scoring-index",
                        cellTemplate: createCellTemplate(
                            scoringIndexId,
                            scoringIndexHref
                        ),
                    });
                }

                columns = [
                    {
                        label: ko.i18n.renderString(
                            "calibrationResults.columnName.questionGroup"
                        ),
                        text: true,
                        id: "questionGroup",
                        class: "calibration-question-group",
                        cellTemplate:
                            '<span data-bind="text: questionGroup"></span>',
                    },
                ].concat(evaluatorColumns);

                self.calibrationResultsGrid().columns(columns);
                /* --  End creation of dataTable columns-- */

                // scoreRows is a map with questionGroup/question ids as keys
                // The values of scoreRows is a map of evaluatorIds to scores.
                // This is used to populate all of the grids in this viewModel.
                const scoreRows = {};

                // Initialize total score
                if (_.isUndefined(scoreRows["evaluation-total"])) {
                    scoreRows["evaluation-total"] = {
                        questionGroup: ko.i18n.renderString(
                            "calibrationResults.rowName.evaluationFormTotal"
                        ),
                    };
                }

                // Initialize question group scores
                _.each(
                    unwrappedCalibration.evaluationForm.questionGroups,
                    function (questionGroup) {
                        if (_.isUndefined(scoreRows[questionGroup.id])) {
                            scoreRows[questionGroup.id] = {
                                questionGroup: questionGroup.name,
                                style: "question-group-row",
                            };
                        }

                        // Recurse down to initialize question scores
                        _.each(questionGroup.questions, function (question) {
                            if (_.isUndefined(scoreRows[question.id])) {
                                scoreRows[question.id] = {
                                    questionGroup: question.text,
                                    questionGroupId: questionGroup.id,
                                    style: "question-row",
                                };
                            }
                        });
                    }
                );

                // Get the data for the grid
                _.each(unwrappedCalibration.evaluations, function (evaluation) {
                    const evaluatorId = evaluation.evaluator.id;
                    aggregateEvaluationData(
                        unwrappedCalibration.evaluationForm,
                        scoreRows,
                        evaluation,
                        evaluatorId
                    );
                });

                if (unwrappedCalibration.scoringIndex) {
                    aggregateEvaluationData(
                        unwrappedCalibration.evaluationForm,
                        scoreRows,
                        unwrappedCalibration.scoringIndex,
                        unwrappedCalibration.scoringIndex.id
                    );
                }

                const evaluationGridData = _.transform(
                    unwrappedCalibration.evaluationForm.questionGroups,
                    function (result, questionGroup) {
                        const questionGroupGridData = _.map(
                            questionGroup.questions,
                            function (question) {
                                return scoreRows[question.id];
                            }
                        );
                        questionGroupGridData.unshift(
                            scoreRows[questionGroup.id]
                        );
                        return result.concat(questionGroupGridData);
                    },
                    []
                );

                // Create the column keys so rows can be built
                const columnKeys = [];
                _.each(columns, function (column) {
                    columnKeys.push(column.id);
                });

                evaluationGridData.unshift(scoreRows["evaluation-total"]);

                const calibrationGridData = buildRows(columnKeys, scoreRows);

                self.calibrationResultsGrid().rows(calibrationGridData);
            } catch (e) {
                if (__DEV__) {
                    loggingService.debug(e);
                }
                toastService.notifyCriticalAlert(
                    "playback.calibrationSummary.fail.msg"
                );
            }
        }
    };

    self.subscribe(self.calibration, function (newCalibration) {
        buildCalibrationGridData(newCalibration);
        self.editCalibrationView(
            self.subView(EditCalibrationView, self.calibration)
        );
    });

    const getCalibration = function () {
        self.isLoading(true);
        qualityManagementService
            .getCalibration(calibrationId, {
                calibratorId: authService.principalId(),
            })
            .then(function (response) {
                self.calibration(new Calibration(response));
            })
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "calibrationResults.getCalibrationRequest",
                })
            )
            .finally(function () {
                self.isLoading(false);
            });
    };
    getCalibration();
});

export default CalibrationResultsView;
